import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import Layout from '../layout/Layout';
import './NotFound.css';

/**
 * Body of the 404 page, without a landmark of its own.
 *
 * Deliberately a plain `div`: Layout already renders `<main id="main-content">`,
 * so emitting another `main` here would nest two landmarks and duplicate the id
 * the skip link targets. The standalone branch below supplies the `main`
 * instead, so exactly one exists either way.
 * @returns {React.ReactElement} The 404 content.
 */
const NotFoundBody: React.FC = () => {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();
  usePageTitle(t('notFound.pageTitle'));

  return (
    <div className="not-found">
      <h1>{t('notFound.heading')}</h1>
      <p>{t('notFound.body')}</p>
      {/* The path is echoed so a mistyped or stale link is self-diagnosing.
          Rendered as text, never as markup, since it is URL-controlled. */}
      <p className="not-found-path">
        <code>{pathname}</code>
      </p>
      <p>
        <Link to="/">{t('notFound.home')}</Link>
      </p>
    </div>
  );
};

/**
 * Catch-all for unmatched routes (#820).
 *
 * Before this existed the router had no `path="*"`, so an unknown URL rendered
 * `<div class="App"></div>` and nothing else: no heading, no landmark, no
 * focusable element, and the document title left over from the previous page.
 * That is a WCAG 2.4.2 failure as much as a UX one -- a screen-reader user got
 * no indication anything had gone wrong, and a keyboard user had nothing to
 * move to. Found by re-probing `/settings` and `/jobs`, which the 2026-08-24
 * evaluation reported as "0 focus stops": neither is a real route (`/account`
 * and `/history` are), so what the probe actually measured was this hole.
 * @returns {React.ReactElement} The 404 page.
 */
const NotFound: React.FC = () => {
  const { isAuthenticated } = useAuth();

  // An authenticated user keeps the app chrome, so a mistyped URL does not also
  // cost them their navigation. An anonymous one gets the bare page, because
  // Layout's nav links all lead to routes they cannot reach.
  return isAuthenticated ? (
    <Layout>
      <NotFoundBody />
    </Layout>
  ) : (
    <main className="not-found-standalone" id="main-content">
      <NotFoundBody />
    </main>
  );
};

export default NotFound;
